import { ComponentType, useState } from 'react';
import {
  Window,
  WindowActionsBar,
  WindowProps
} from '@progress/kendo-react-dialogs';
import { FluidForm } from '../../../components/forms';
import { ResourcePackEntity } from '../../../types/game-document/';
import {
  CheckboxChangeEvent,
  InputChangeEvent
} from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { EditorMode } from '../../../types/editor-mode';
import { EntityEditor } from '../../../types/game-document/entity-editor';
import { ResourcePackEditor } from './resource-pack-editor';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';

interface ResourcePackWindowProps extends WindowProps {
  editorEntity: EntityEditor<ResourcePackEntity>;
  toggleDialog: Function;
  onSubmit: (
    editorEntity: EntityEditor<ResourcePackEntity>,
    isCopyOriginal: boolean,
    isTranslateUsingAI: boolean,
    displayLanguage: string
  ) => void;
  editorMode: EditorMode;
  isLanguageCombobox?: boolean;
  listedLanguages?: string[];
  allowAddNew?: boolean;
  // listedDisplayLanguages?: string[];
}

// Need to move somewhere else for other component to import
export interface RequiredInput {
  name: string;
  errorMessage: string;
}

export const ResourcePackEditorWindow: ComponentType<
  ResourcePackWindowProps
> = ({
  editorEntity,
  toggleDialog,
  onSubmit,
  editorMode,
  isLanguageCombobox,
  listedLanguages,
  allowAddNew,
  // listedDisplayLanguages,
  ...props
}: ResourcePackWindowProps) => {
  const [entity, setEntity] = useState<ResourcePackEntity>({
    ...editorEntity.entity
  });

  const [isCopyOriginal, setIsCopyOriginal] = useState<boolean>(false);
  const [isTranslateUsingAI, setIsTranslateUsingAI] = useState<boolean>(false);
  const [disableCopyOriginal, setDisableCopyOriginal] =
    useState<boolean>(false);
  const [disableTranslateAI, setDisableTranslateUsingAI] =
    useState<boolean>(false);
  const [isLanguageDuplicate, setIsLanguageDuplicate] =
    useState<boolean>(false);

  const handleInputChange = (event: InputChangeEvent) => {
    let newEntity: ResourcePackEntity = { ...entity };
    newEntity.name = event?.target?.value?.toString() ?? '';
    setEntity(newEntity);
  };

  const handleCheckedChange = (event: CheckboxChangeEvent) => {
    const toggleValue = event?.target?.value as boolean;
    setIsCopyOriginal(toggleValue);
    setDisableTranslateUsingAI(toggleValue);
  };

  const handleTranslateAIChange = (event: CheckboxChangeEvent) => {
    const toggleValue = event?.target?.value as boolean;
    setIsTranslateUsingAI(toggleValue);
    setDisableCopyOriginal(toggleValue);
  };

  const handleComboChange = (event: ComboBoxChangeEvent) => {
    let newEntity: ResourcePackEntity = { ...entity };
    newEntity.name = event?.target?.value?.toString() ?? '';

    if (listedLanguages && listedLanguages.length > 0) {
      setIsLanguageDuplicate(
        listedLanguages?.find((item) => item === newEntity.name!) ? true : false
      );
    }

    setEntity(newEntity);
  };
  const handleDisplayLanguageChange = (event: ComboBoxChangeEvent) => {
    let newEntity: ResourcePackEntity = { ...entity };

    newEntity.displayLanguage = event?.target?.value?.toString() ?? '';

    setEntity(newEntity);
  };

  return (
    <Window
      modal={true}
      minimizeButton={undefined}
      className={'shadow'}
      initialWidth={500}
      initialHeight={editorEntity.isNew ? 335 : 280}
      title={editorEntity.isNew ? 'Language title' : editorEntity.entity.name}
      {...props}>
      <FluidForm>
        <ResourcePackEditor
          editorMode={editorMode}
          entity={entity}
          isNew={editorEntity.isNew}
          handleInputChange={handleInputChange}
          handleCheckedChange={handleCheckedChange}
          handleTranslateAIChange={handleTranslateAIChange}
          handleComboChange={handleComboChange}
          handleDisplayLanguageChange={handleDisplayLanguageChange}
          disableCopyOriginal={disableCopyOriginal}
          disableTranslateAI={disableTranslateAI}
          isLanguageCombobox={isLanguageCombobox}
          isLanguageDuplicate={isLanguageDuplicate}
          allowAddNew={allowAddNew}
        />
      </FluidForm>
      <WindowActionsBar>
        <Button
          themeColor={'secondary'}
          onClick={() => {
            toggleDialog();
          }}>
          Cancel
        </Button>
        <Button
          disabled={
            isLanguageDuplicate || !entity.name || !entity.displayLanguage
          }
          themeColor={'primary'}
          onClick={() => {
            onSubmit(
              { isNew: editorEntity.isNew, entity },
              isCopyOriginal,
              isTranslateUsingAI,
              entity.displayLanguage!
            );
          }}>
          Save
        </Button>
      </WindowActionsBar>
    </Window>
  );
};
